import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Edit3, AlertCircle } from 'lucide-react';
import { bulkUpdateMetaPagesConfig, getPageCategories, createPageCategory } from '@/services/api';
import { TIMEZONES, LANGUAGES } from '../constants/config-options';
import { SearchableSelect } from '@/components/ui/searchable-select';
import type { MetaPage } from '../types/meta.types';
import type { PageCategory } from '../types/meta.types';

interface BulkUpdatePagesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** IDs internos (chatmeta_pages.id) selecionados na tabela. */
    pageIds: number[];
    /** Callback após sucesso — usado pra limpar seleção e refetch. */
    onSuccess?: () => void;
}

/**
 * Modal de atualização em massa de páginas.
 *
 * Aceita 3 campos opcionais: Categoria, Idioma e Fuso horário.
 * Campos não preenchidos (== sentinel `__unchanged__`) ficam intocados na request.
 *
 * Backend: PATCH /api/v1/meta/pages/bulk-config (max 500 ids por chamada).
 */
export default function BulkUpdatePagesDialog({
    open,
    onOpenChange,
    pageIds,
    onSuccess,
}: BulkUpdatePagesDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Valor sentinel "__unchanged__" indica "não tocar nesse campo".
    // Diferente de string vazia, pq vazio em <Select> = limpar.
    const UNCHANGED = '__unchanged__';
    const [categoryId, setCategoryId] = useState<string>(UNCHANGED);
    const [language, setLanguage] = useState<string>(UNCHANGED);
    const [timezone, setTimezone] = useState<string>(UNCHANGED);

    // Reset state ao abrir/fechar pra evitar carry-over entre operações
    useEffect(() => {
        if (open) {
            setCategoryId(UNCHANGED);
            setLanguage(UNCHANGED);
            setTimezone(UNCHANGED);
        }
    }, [open]);

    const { data: categories = [] } = useQuery({
        queryKey: ['page-categories'],
        queryFn: getPageCategories,
        staleTime: 1000 * 60 * 5,
    });

    const hasAnyChange = categoryId !== UNCHANGED || language !== UNCHANGED || timezone !== UNCHANGED;

    const mutation = useMutation({
        mutationFn: () => {
            const payload: any = { page_ids: pageIds };
            if (categoryId !== UNCHANGED) payload.internal_category_id = parseInt(categoryId);
            if (language !== UNCHANGED) payload.language = language;
            if (timezone !== UNCHANGED) payload.timezone = timezone;
            return bulkUpdateMetaPagesConfig(payload);
        },
        onSuccess: async (data) => {
            const skipped = data.skipped?.length ?? 0;

            // ─── Optimistic update no cache local ─────────────────────
            // Sem isso, a UI fica com dados velhos até o refetch terminar
            // (~200ms-1s). Aplicar local primeiro → feedback instantâneo.
            // Resolve internal_category_name pelo cache de page-categories
            // pra não mostrar "—" enquanto o servidor responde.
            const skippedSet = new Set<number>(data.skipped ?? []);
            const newCategoryId = categoryId !== UNCHANGED ? parseInt(categoryId) : null;
            // Resolve pelo cache MAIS RECENTE do react-query (não o `categories`
            // do useState, que pode estar desatualizado se o user criou categoria
            // nova via SearchableSelect.onCreate logo antes de aplicar).
            const liveCats = queryClient.getQueryData<PageCategory[]>(['page-categories']) ?? categories;
            const newCategoryName = newCategoryId
                ? (liveCats.find((c) => c.id === newCategoryId)?.name ?? null)
                : null;

            queryClient.setQueryData<MetaPage[]>(['meta-pages'], (old) => {
                if (!old) return old;
                return old.map((p) => {
                    // Skip páginas que o servidor rejeitou (sem permissão)
                    if (!pageIds.includes(p.id) || skippedSet.has(p.id)) return p;
                    const updated: MetaPage = { ...p };
                    if (categoryId !== UNCHANGED) {
                        updated.internal_category_id = newCategoryId;
                        updated.internal_category_name = newCategoryName;
                    }
                    if (language !== UNCHANGED) updated.language = language;
                    if (timezone !== UNCHANGED) updated.timezone = timezone;
                    return updated;
                });
            });

            // Refetch silencioso pra garantir consistência (caso o servidor
            // tenha enriquecido outros campos relacionados que o optimistic
            // não cobre — group/apps/etc).
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            queryClient.invalidateQueries({ queryKey: ['pages'] });

            toast({
                title: 'Páginas atualizadas',
                description: `${data.updated} página(s) atualizada(s)${skipped > 0 ? ` · ${skipped} ignorada(s) (sem permissão)` : ''}.`,
            });

            onSuccess?.();
            onOpenChange(false);
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar em massa',
                description: err?.response?.data?.detail || err?.message || 'Tente novamente.',
            });
        },
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Edit3 className="w-5 h-5 text-blue-600" />
                        Atualizar em massa
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="bg-blue-600 text-white border-blue-600 font-semibold">
                            {pageIds.length}
                        </Badge>
                        <span>{pageIds.length === 1 ? 'página será atualizada' : 'páginas serão atualizadas'}.</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-5 py-2">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-900">
                            Campos deixados em <strong>Não alterar</strong> permanecem como estão em cada página.
                            Apenas os campos escolhidos serão sobrescritos.
                        </p>
                    </div>

                    {/* Categoria — usa SearchableSelect com `onCreate` pra permitir
                        digitar uma categoria nova e criá-la inline (POST /page-categories).
                        Quando criada, a categoria nova é automaticamente selecionada. */}
                    <div className="grid gap-2">
                        <Label htmlFor="bulk-category" className="text-sm font-semibold text-gray-700">
                            Categoria
                        </Label>
                        <SearchableSelect
                            value={categoryId}
                            onValueChange={setCategoryId}
                            options={[
                                { value: UNCHANGED, label: 'Não alterar' },
                                ...categories.map((cat: any) => ({ value: String(cat.id), label: cat.name })),
                            ]}
                            placeholder="Selecione uma categoria"
                            searchPlaceholder="Buscar ou digitar nova..."
                            createLabel="Criar categoria"
                            onCreate={async (name) => {
                                try {
                                    const created = await createPageCategory({ name });
                                    // Atualiza cache de categorias pra refletir em outras telas
                                    await queryClient.invalidateQueries({ queryKey: ['page-categories'] });
                                    toast({
                                        title: 'Categoria criada',
                                        description: `"${created.name}" foi adicionada e selecionada.`,
                                    });
                                    return { value: String(created.id), label: created.name };
                                } catch (err: any) {
                                    toast({
                                        variant: 'destructive',
                                        title: 'Erro ao criar categoria',
                                        description: err?.response?.data?.detail || 'Tente novamente.',
                                    });
                                    throw err;
                                }
                            }}
                        />
                    </div>

                    {/* Idioma */}
                    <div className="grid gap-2">
                        <Label htmlFor="bulk-language" className="text-sm font-semibold text-gray-700">
                            Idioma
                        </Label>
                        <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger id="bulk-language" className="rounded-xl border-gray-200">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white rounded-xl border-gray-100 shadow-xl">
                                <SelectItem value={UNCHANGED} className="rounded-lg text-gray-400">
                                    Não alterar
                                </SelectItem>
                                {LANGUAGES.map((lang) => (
                                    <SelectItem key={lang.value} value={lang.value} className="rounded-lg">
                                        {lang.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Fuso horário */}
                    <div className="grid gap-2">
                        <Label htmlFor="bulk-timezone" className="text-sm font-semibold text-gray-700">
                            Fuso horário
                        </Label>
                        <Select value={timezone} onValueChange={setTimezone}>
                            <SelectTrigger id="bulk-timezone" className="rounded-xl border-gray-200">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white rounded-xl border-gray-100 shadow-xl max-h-72">
                                <SelectItem value={UNCHANGED} className="rounded-lg text-gray-400">
                                    Não alterar
                                </SelectItem>
                                {TIMEZONES.map((tz) => (
                                    <SelectItem key={tz.value} value={tz.value} className="rounded-lg">
                                        {tz.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        onClick={() => mutation.mutate()}
                        disabled={!hasAnyChange || mutation.isPending || pageIds.length === 0}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {mutation.isPending ? 'Aplicando...' : 'Aplicar a todas'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
